"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Copy,
  Loader2,
  ShieldCheck,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { faToman, formatCard, faDateTime } from "@/lib/format";
import type { PaymentWithRelations, Subscription } from "./types";

type Props = {
  payment: PaymentWithRelations | null;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
};

type Phase = "form" | "success" | "error";

export default function ApproveDialog({ payment, open, onClose, onDone }: Props) {
  const [vlessLink, setVlessLink] = React.useState("");
  const [adminNote, setAdminNote] = React.useState("");
  const [rejectNote, setRejectNote] = React.useState("");
  const [showReject, setShowReject] = React.useState(false);
  const [loading, setLoading] = React.useState<"approve" | "reject" | null>(null);
  const [phase, setPhase] = React.useState<Phase>("form");
  const [resultSub, setResultSub] = React.useState<Subscription | null>(null);

  // Reset everything when dialog opens with a new payment
  React.useEffect(() => {
    if (open) {
      setVlessLink("");
      setAdminNote("");
      setRejectNote("");
      setShowReject(false);
      setLoading(null);
      setPhase("form");
      setResultSub(null);
    }
  }, [open, payment?.id]);

  if (!payment) return null;

  async function submitApprove() {
    if (!payment) return;
    setLoading("approve");
    try {
      const res = await fetch("/api/admin/payments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: payment.id,
          action: "approve",
          adminNote: adminNote.trim() || undefined,
          vlessLink: vlessLink.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در تأیید پرداخت");
      }
      setResultSub((data.subscription as Subscription) ?? null);
      setPhase("success");
      toast.success("اشتراک فعال شد!");
      onDone();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در تأیید پرداخت";
      toast.error(msg);
      setPhase("error");
    } finally {
      setLoading(null);
    }
  }

  async function submitReject() {
    if (!payment) return;
    if (!rejectNote.trim()) {
      toast.error("برای رد پرداخت، دلیل را وارد کنید.");
      return;
    }
    setLoading("reject");
    try {
      const res = await fetch("/api/admin/payments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: payment.id,
          action: "reject",
          adminNote: rejectNote.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در رد پرداخت");
      }
      toast.success("پرداخت رد شد.");
      onDone();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در رد پرداخت";
      toast.error(msg);
    } finally {
      setLoading(null);
    }
  }

  function closeDialog() {
    if (loading) return;
    onClose();
  }

  function copyLink() {
    if (!resultSub?.vlessLink) return;
    navigator.clipboard
      .writeText(resultSub.vlessLink)
      .then(() => toast.success("لینک کپی شد."))
      .catch(() => toast.error("کپی لینک ناموفق بود."));
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeDialog()}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="size-5 text-primary" />
            {phase === "success"
              ? "اشتراک با موفقیت فعال شد"
              : showReject
                ? "رد پرداخت"
                : "تأیید و فعال‌سازی اشتراک"}
          </DialogTitle>
          <DialogDescription className="text-right">
            {phase === "success"
              ? "لینک اشتراک کاربر در ادامه نمایش داده شده است."
              : "جزئیات پرداخت را بررسی کنید و در صورت صحت، آن را تأیید کنید."}
          </DialogDescription>
        </DialogHeader>

        {/* Payment summary */}
        <div className="rounded-xl border bg-muted/40 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">کاربر:</span>
              <span className="font-medium">{payment.user.name}</span>
            </div>
            <span dir="ltr" className="text-sm text-muted-foreground">
              {payment.user.phone}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">طرح</span>
              <span className="font-medium">{payment.plan.name}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">مبلغ</span>
              <span className="font-medium text-primary">
                {faToman(payment.amountToman)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">کارت فرستنده</span>
              <span dir="ltr" className="font-mono text-xs">
                {payment.senderCard ? formatCard(payment.senderCard) : "—"}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">تاریخ</span>
              <span className="text-xs">{faDateTime(payment.createdAt)}</span>
            </div>
            {payment.trackingCode && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-muted-foreground">کد پیگیری</span>
                <span dir="ltr" className="font-mono text-xs">
                  {payment.trackingCode}
                </span>
              </div>
            )}
          </div>

          {payment.receiptImage && (
            <a
              href={payment.receiptImage}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 block overflow-hidden rounded-lg border bg-background"
            >
              <img
                src={payment.receiptImage}
                alt="تصویر رسید"
                className="max-h-44 w-full object-contain"
              />
            </a>
          )}
        </div>

        {phase === "form" && !showReject && (
          <>
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs leading-relaxed text-muted-foreground">
              اگر تنظیمات 3x-ui وارد شده باشد، کانفیگ به‌صورت خودکار ساخته می‌شود.
              در غیر این صورت لینک را دستی وارد کنید.
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="approve-vless">لینک VLESS (اختیاری)</Label>
              <Input
                id="approve-vless"
                dir="ltr"
                placeholder="vless://... — در صورت خالی بودن، از 3x-ui ساخته می‌شود"
                value={vlessLink}
                onChange={(e) => setVlessLink(e.target.value)}
                disabled={loading !== null}
                className="text-left font-mono text-xs"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="approve-note">یادداشت ادمین (اختیاری)</Label>
              <Textarea
                id="approve-note"
                placeholder="مثلاً: پرداخت بررسی و تأیید شد."
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                disabled={loading !== null}
                className="min-h-16 text-sm"
              />
            </div>

            <Separator />

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button
                type="button"
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={loading !== null}
                onClick={() => setShowReject(true)}
              >
                <X className="size-4" />
                رد پرداخت
              </Button>
              <Button
                type="button"
                onClick={submitApprove}
                disabled={loading !== null}
                className="bg-primary hover:bg-primary/90"
              >
                {loading === "approve" ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    در حال فعال‌سازی...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="size-4" />
                    تأیید و فعال‌سازی
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {phase === "form" && showReject && (
          <>
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs leading-relaxed text-destructive">
              با رد این پرداخت، اشتراکی برای کاربر ساخته نمی‌شود. لطفاً دلیل رد را
              به‌وضحت وارد کنید تا کاربر مطلع شود.
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="reject-note">دلیل رد *</Label>
              <Textarea
                id="reject-note"
                placeholder="مثلاً: رسید نامعتبر است / مبالغ ناقص است."
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                disabled={loading !== null}
                className="min-h-20 text-sm"
              />
            </div>

            <Separator />

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-start">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReject(false)}
                disabled={loading !== null}
              >
                بازگشت
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={submitReject}
                disabled={loading !== null}
              >
                {loading === "reject" ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    در حال رد...
                  </>
                ) : (
                  <>
                    <X className="size-4" />
                    تأیید رد
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {phase === "success" && (
          <>
            <div className="flex flex-col items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-5 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-primary/15">
                <CheckCircle2 className="size-6 text-primary" />
              </div>
              <div className="text-sm">
                اشتراک <span className="font-bold">{payment.plan.name}</span> برای{" "}
                <span className="font-bold">{payment.user.name}</span> فعال شد.
              </div>
              {resultSub?.vlessLink ? (
                <div className="w-full">
                  <Label className="mb-1 block text-xs text-muted-foreground">
                    لینک اشتراک (VLESS)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      dir="ltr"
                      readOnly
                      value={resultSub.vlessLink}
                      className="text-left font-mono text-xs"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={copyLink}
                      aria-label="کپی لینک"
                    >
                      <Copy className="size-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="w-full rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-600 dark:text-amber-400">
                  لینک VLESS ساخته نشد (احتمالاً تنظیمات 3x-ui کامل نیست). می‌توانید
                  بعداً ویرایش کنید.
                </div>
              )}
              {resultSub?.endDate && (
                <div className="text-xs text-muted-foreground">
                  اعتبار تا: {faDateTime(resultSub.endDate)}
                </div>
              )}
            </div>

            <Separator />

            <div className="flex justify-end">
              <Button type="button" variant="outline" onClick={closeDialog}>
                بستن
              </Button>
            </div>
          </>
        )}

        {phase === "error" && (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/10 p-5 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-destructive/15">
              <X className="size-6 text-destructive" />
            </div>
            <div className="text-sm">
              خطایی در هنگام تأیید رخ داد. لطفاً دوباره تلاش کنید.
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPhase("form")}
            >
              تلاش مجدد
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
